import { PaymentRequestButtonElement, useStripe } from '@stripe/react-stripe-js'
import { PaymentRequest } from '@stripe/stripe-js'
import React, { useContext, useEffect, useState } from 'react'
import { useMutation } from '@apollo/client'

import { getTotalPrice } from 'utils/Upgrade'
import PurchaseIntentMutation from 'components/Upgrade/API/PurchaseIntentMutation.gql'
import ConfirmIntentMutation from 'components/Upgrade/API/ConfirmIntentMutation.gql'
import { trackEvent } from 'utils/Analytics'
import { AppContext } from 'contexts/AppContext'
import { ApplePayButtonContainer, Or, OrLine } from './ApplePay.styles'

export interface ApplePayProps {
  countryCode?: string
  currency?: string
  // TODO: should generate this from the GraphQL
  stripePlan: TODO
  errorText?: string
  setErrorText(errorText: string): void
  clearErrorText(): void
  disableSubmit?: boolean
  setDisableSubmit(disabled: boolean): void
  onPurchaseComplete(): void
  flow: string
  conversionType: string
}

export function ApplePay({
  countryCode,
  currency,
  stripePlan,
  errorText,
  setErrorText,
  clearErrorText,
  disableSubmit,
  setDisableSubmit,
  onPurchaseComplete,
  flow,
  conversionType,
}: ApplePayProps) {
  const { domain } = useContext(AppContext)
  const stripe = useStripe()

  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(null)
  const { id: stripePlanId, display_name: displayName } = stripePlan
  const totalAmount = getTotalPrice(stripePlan)

  const addPaymentRequestListener = async () => {
    paymentRequest.on('paymentmethod', async (ev) => {
      setDisableSubmit(true)
      clearErrorText()

      try {
        const response = await purchaseIntentMutationFunction({
          variables: {
            nameOnCard: ev?.payerName,
            paymentMethodId: ev?.paymentMethod?.id,
            stripePlanId: stripePlanId,
            amount: Math.floor(totalAmount),
            currency: currency,
            flow,
            conversionType,
          },
        })
        if (isApplePayComplete(response.data?.purchase_with_intent)) {
          ev.complete('success')
        } else if (isBadResponse(response.data?.purchase_with_intent)) {
          ev.complete('fail')
        }
      } catch (error) {
        ev.complete('fail')
      }
    })

    return true
  }

  useEffect(() => {
    if (stripe && countryCode && currency && displayName && totalAmount) {
      const paymentResponse = stripe.paymentRequest({
        country: countryCode,
        currency: currency,
        total: {
          label: `${displayName} Total`,
          amount: totalAmount,
        },
        requestPayerName: true,
      })

      paymentResponse.canMakePayment().then((result) => {
        if (result && result.applePay) {
          setPaymentRequest(paymentResponse)
        }
      })
    }
  }, [stripe, countryCode, currency, displayName, totalAmount])

  useEffect(() => {
    if (paymentRequest) {
      addPaymentRequestListener()
    }
  }, [paymentRequest, addPaymentRequestListener])

  const [purchaseIntentMutationFunction, { error: purchaseIntentMutationError }] = useMutation(
    PurchaseIntentMutation,
    {
      onCompleted({ purchase_with_intent }) {
        confirmPayment(purchase_with_intent)
      },
    }
  )

  const [confirmIntentMutationFunction, { error: confirmIntentMutationError }] = useMutation(
    ConfirmIntentMutation,
    {
      onCompleted({ confirm_intent }) {
        confirmPayment(confirm_intent)
      },
    }
  )

  const error = purchaseIntentMutationError || confirmIntentMutationError

  if (error) {
    const errorMessage =
      error.graphQLErrors === null || error.graphQLErrors.length === 0
        ? error.message
        : error.graphQLErrors[0].message
    if (errorText !== errorMessage) {
      setErrorText(errorMessage)
    }
    if (disableSubmit !== false) {
      setDisableSubmit(false)
    }
  }

  const isBadResponse = (data) => {
    const clientSecret = data?.payment_intent_client_secret
    const intentID = data?.payment_intent_id
    const stripeSubscriptionID = data?.stripe_subscription_id
    return !intentID || !clientSecret || !stripeSubscriptionID
  }

  const isApplePayComplete = (data) => {
    const requiresAction = data?.requires_action
    return !isBadResponse(data) && requiresAction === false
  }

  const confirmPayment = (data) => {
    const nameOnCard = data?.name_on_card
    const clientSecret = data?.payment_intent_client_secret
    const intentID = data?.payment_intent_id
    const stripeSubscriptionID = data?.stripe_subscription_id

    if (isBadResponse(data)) {
      return
    }

    if (isApplePayComplete(data)) {
      trackEvent(domain, 'Subscription Created', {
        stripePlanId,
        paymentMethod: 'Apple Pay',
      })
      onPurchaseComplete()
      return null
    }

    stripe.handleCardAction(clientSecret).then(async (result) => {
      if (!result.error) {
        try {
          await confirmIntentMutationFunction({
            variables: {
              paymentIntentID: intentID,
              nameOnCard: nameOnCard,
              stripeSubscriptionID: stripeSubscriptionID,
            },
          })
        } catch (error) {}
      }
    })
  }

  if (!paymentRequest) {
    return null
  }

  return (
    <>
      <ApplePayButtonContainer>
        <PaymentRequestButtonElement options={{ paymentRequest }} />
      </ApplePayButtonContainer>
      <OrLine>
        <span>
          <Or>or</Or>
        </span>
      </OrLine>
    </>
  )
}
